// src/components/VisitorCounting.js
// src/components/VisitorCounting.js - FIXED VERSION
import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "./VisitorCounting.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const VisitorCounting = () => {
  // State for visitor counts
  const [visitorData, setVisitorData] = useState({
    dailyCount: 0,
    yearlyCount: 0,
    totalCount: 0,
    history: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Function to fetch visitor data from the server
  const fetchVisitorData = async () => {
    try {
      setError(null);
      const response = await fetch(`${apiUrl}/api/visitor`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setVisitorData({
        dailyCount: data.dailyCount || 0,
        yearlyCount: data.yearlyCount || 0,
        totalCount: data.totalCount || 0,
        history: data.history || []
      });
      
      console.log("Dashboard data fetched:", data);
    } catch (err) {
      console.error("Error fetching visitor data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (apiUrl) {
      fetchVisitorData();

      // Refresh the visitor data every 10 seconds
      const interval = setInterval(() => {
        fetchVisitorData();
      }, 10000);

      return () => clearInterval(interval);
    } else {
      setError("API URL not configured");
      setLoading(false);
    }
  }, [apiUrl]);

  // Reset visitor counts by calling the server endpoint
  const handleReset = async () => {
    if (window.confirm("Are you sure you want to reset all visitor counts? This action cannot be undone.")) {
      try {
        const response = await fetch(`${apiUrl}/api/visitor/reset`, {
          method: "POST",
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        setVisitorData({
          dailyCount: data.dailyCount || 0,
          yearlyCount: data.yearlyCount || 0,
          totalCount: data.totalCount || 0,
          history: data.history || []
        });
        
        alert("Visitor counts have been reset successfully!");
        console.log("Reset successful:", data);
      } catch (err) {
        console.error("Error resetting visitor data:", err);
        alert("Error resetting visitor counts: " + err.message);
      }
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Prepare chart data using the fetched history
  const chartData = {
    labels: visitorData.history.map((item) => formatDate(item.date)),
    datasets: [
      {
        label: "Daily Visitors",
        data: visitorData.history.map((item) => item.count),
        backgroundColor: "rgba(26, 35, 115, 0.2)",
        borderColor: "rgba(26, 35, 115, 1)",
        borderWidth: 2,
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Daily Visitor Trend (Last 7 Days)',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          callback: function(value) {
            return Number.isInteger(value) ? value : '';
          }
        },
      },
    },
  };

  if (loading) {
    return (
      <div className="visitor-counting-container">
        <h2>Visitor Counting Dashboard</h2>
        <p>Loading visitor data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="visitor-counting-container">
        <h2>Visitor Counting Dashboard</h2>
        <div className="error-message" style={{ color: 'red', padding: '10px', backgroundColor: '#ffe6e6', border: '1px solid #ff9999', borderRadius: '4px' }}>
          <p><strong>Error:</strong> {error}</p>
          <button onClick={fetchVisitorData} style={{ marginTop: '10px', padding: '5px 10px' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="visitor-counting-container">
      <h2>Visitor Counting Dashboard</h2>
      
      <div className="count-boxes">
        <div className="count-box">
          <h3>Today's Visitors</h3>
          <p>{visitorData.dailyCount}</p>
        </div>
        <div className="count-box">
          <h3>This Year</h3>
          <p>{visitorData.yearlyCount}</p>
        </div>
        <div className="count-box">
          <h3>All Time</h3>
          <p>{visitorData.totalCount}</p>
        </div>
      </div>
      
      <div className="line-graph">
        <h3>Visitor Trend</h3>
        {visitorData.history && visitorData.history.length > 0 ? (
          <div style={{ height: '400px' }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f9f9f9', border: '1px solid #ddd', borderRadius: '8px' }}>
            <p>No historical data available for chart display</p>
            <small>Data will appear as visitors are tracked over multiple days</small>
          </div>
        )}
      </div>
      
      <div className="reset-container">
        <button onClick={handleReset}>Reset All Visitor Counts</button>
      </div>
      
      {/* Debug info - remove in production */}
      <div style={{ marginTop: '20px', fontSize: '12px', color: '#666', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
        <strong>Debug Info:</strong>
        <br />Last updated: {new Date().toLocaleTimeString()}
        <br />History entries: {visitorData.history.length}
        <br />API URL: {apiUrl}
      </div>
    </div>
  );
};

export default VisitorCounting;